import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Text,
  useToast,
} from "@chakra-ui/react";
import { getEmployeeName } from "../../Config/Employees";
import EmployeeDataService from "../../services/Employee";
import { DateTime } from "luxon";
import { getUserAccess } from "../../utils/authUtils";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";
import { HrmsCard } from "../../components/Hrms/HrmsCard";
import { HrmsTable } from "../../components/Hrms/HrmsTable";
import { HrmsStatCard } from "../../components/Hrms/HrmsStatCard";
import { formatHrmsDate, PersonCell } from "../../components/Hrms/hrmsUi";
import { formatHoursMinutes } from "./attendanceFormat";

interface AttendanceRecord {
  _id: string;
  name: string;
  email: string;
  date: string;
  loginTime: string;
  logoutTime: string;
  isLate?: boolean;
  lateByMinutes?: number;
  isEarlyExit?: boolean;
  earlyExitByMinutes?: number;
  overtimeMinutes?: number;
  dayType?: "full" | "half" | "absent" | "present" | "punch_missing";
}

const DAY_TYPE_BADGE: Record<
  NonNullable<AttendanceRecord["dayType"]>,
  { label: string; color: string }
> = {
  full: { label: "Full day", color: "green" },
  half: { label: "Half day", color: "orange" },
  present: { label: "Present", color: "green" },
  punch_missing: { label: "Log-out missing", color: "orange" },
  absent: { label: "Absent", color: "red" },
};

interface TodayAttendance {
  isLoggedIn: boolean;
  loginTime: string | null;
  logoutTime: string | null;
}

interface TeamMember {
  email: string;
  name: string;
}

/**
 * "own": the signed-in employee's attendance (My Attendance).
 * "team": a Manager's direct reports only, or everyone's for
 * attendance.view.all holders (Admin, HR Viewer) — My Team Attendance.
 * Both open on the current month.
 */
export const EmployeeAttendance = ({ view = "own" }: { view?: "own" | "team" }) => {
  const isTeamView = view === "team";
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const userName = getEmployeeName(userEmail);
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [todayAttendance, setTodayAttendance] = useState<TodayAttendance>({
    isLoggedIn: false,
    loginTime: null,
    logoutTime: null,
  });
  const [attendanceHistory, setAttendanceHistory] = useState<
    AttendanceRecord[]
  >([]);
  
  const [allAttendance, setAllAttendance] = useState<AttendanceRecord[]>([]);

  const [selectedMonth, setSelectedMonth] = useState(DateTime.now().toFormat("MM"));
  const [selectedYear, setSelectedYear] = useState(String(DateTime.now().year));
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  const { canViewAllEmployees, canViewTeam } = getUserAccess();
  const canViewScopedList = canViewAllEmployees || canViewTeam;
  const showTeamList = isTeamView && canViewScopedList;

  
  const generateMonthYearArray = () => {
    try {
      const startDate = DateTime.fromObject({
        month: 11,
        year: 2025,
      });
      const endDate = DateTime.now();
      let currentDate = startDate;
      const monthYearArray = [];

      while (currentDate <= endDate) {
        monthYearArray.push({
          month: currentDate.toFormat("MM"),
          year: currentDate.year.toString(),
          text: `${currentDate.toFormat("MMMM")} ${currentDate.year}`,
        });
        currentDate = currentDate.plus({ months: 1 });
      }

      return monthYearArray.reverse();
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  const fetchTodayAttendance = async () => {
    try {
      const res = await EmployeeDataService.getTodayAttendance(
        token,
        userEmail,
      );
      if (res.data.status === "success") {
        setTodayAttendance(res.data.data);
      }
    } catch (e: any) {
      console.error("Error fetching today's attendance:", e);
    }
  };

  const fetchAttendanceHistory = async () => {
    try {
      const res = await EmployeeDataService.getEmployeeAttendanceHistory(
        token,
        userEmail,
        userEmail,
        selectedMonth,
        selectedYear,
      );
      if (res.data.status === "success") {
        const records: AttendanceRecord[] = res.data.data || [];
        setAttendanceHistory(
          selectedDate ? records.filter((r) => r.date === selectedDate) : records,
        );
      }
    } catch (e: any) {
      console.error("Error fetching attendance history:", e);
    }
  };

  // People the caller may filter by: a Manager's direct reports, or every
  // current employee for attendance.view.all holders.
  const fetchTeamMembers = async () => {
    try {
      const res = await EmployeeDataService.getTeamLeaveTypeBalance(token, userEmail);
      const members: TeamMember[] = (res.data.data || []).map((m: any) => ({
        email: m.email,
        name: m.name || m.email,
      }));
      setTeamMembers(members);
    } catch (e: any) {
      console.error("Error fetching team members:", e);
    }
  };

  const fetchAllAttendance = async () => {
    if (!showTeamList) return;
    try {
      const res = await EmployeeDataService.getAllAttendance(
        token,
        userEmail,
        selectedEmail,
        selectedMonth,
        selectedYear,
        selectedDate,
      );
      if (res.data.status === "success") {
        const records: AttendanceRecord[] = res.data.data || [];
        // The backend's team scope includes the Manager themselves; this page
        // lists only their team (their own records live in My Attendance).
        setAllAttendance(
          canViewAllEmployees ? records : records.filter((r) => r.email !== userEmail),
        );
      }
    } catch (e: any) {
      console.error("Error fetching all attendance:", e);
    }
  };

  const handleLogin = async () => {
    setIsActionLoading(true);
    try {
      const res = await EmployeeDataService.loginAttendance(
        token,
        userEmail,
        userName,
      );
      if (res.data.status === "success") {
        toast({
          position: "top",
          title: "Login Successful",
          description: "Your attendance has been marked.",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        fetchTodayAttendance();
      }
    } catch (e: any) {
      toast({
        position: "top",
        title: "Error",
        description: e.response?.data?.message || "Failed to mark attendance.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleLogout = async () => {
    setIsActionLoading(true);
    try {
      const res = await EmployeeDataService.logoutAttendance(token, userEmail);
      if (res.data.status === "success") {
        toast({
          position: "top",
          title: "Logout Successful",
          description: "Your logout has been recorded.",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        fetchTodayAttendance();
      }
    } catch (e: any) {
      toast({
        position: "top",
        title: "Error",
        description: e.response?.data?.message || "Failed to mark logout.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const formatTime = (isoString: string | null) => {
    if (!isoString) return "-";
    return DateTime.fromISO(isoString).toFormat("hh:mm a");
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return DateTime.fromISO(dateString).toFormat("dd MMM yyyy");
  };

  const calculateDuration = (loginTime: string, logoutTime: string) => {
    if (!loginTime || !logoutTime) return "-";
    const login = DateTime.fromISO(loginTime);
    const logout = DateTime.fromISO(logoutTime);
    const diff = logout.diff(login, ["hours", "minutes"]);
    return `${Math.floor(diff.hours)}h ${Math.floor(diff.minutes)}m`;
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      if (isTeamView) {
        await Promise.all([fetchAllAttendance(), fetchTeamMembers()]);
      } else {
        await fetchTodayAttendance();
        await fetchAttendanceHistory();
      }
      setIsLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const search = () => (isTeamView ? fetchAllAttendance() : fetchAttendanceHistory());
  const records = isTeamView ? allAttendance : attendanceHistory;
  const nameByEmail = Object.fromEntries(teamMembers.map((m) => [m.email, m.name]));

  const minutesWorked = (r: AttendanceRecord) =>
    r.loginTime && r.logoutTime
      ? DateTime.fromISO(r.logoutTime).diff(DateTime.fromISO(r.loginTime), "minutes").minutes
      : 0;
  const completed = records.filter((r) => r.loginTime && r.logoutTime);
  const avgMinutes = completed.length
    ? completed.reduce((sum, r) => sum + minutesWorked(r), 0) / completed.length
    : 0;
  const stats = [
    {
      label: "Days present",
      value: records.filter((r) => r.dayType && r.dayType !== "absent").length,
      help: `${records.filter((r) => r.dayType === "half").length} half days`,
    },
    {
      label: "Late arrivals",
      value: records.filter((r) => r.isLate).length,
      accent: "#dd6b20",
    },
    {
      label: "Early exits",
      value: records.filter((r) => r.isEarlyExit).length,
      accent: "#dd6b20",
    },
    {
      label: "Avg. hours / day",
      value: avgMinutes ? `${Math.floor(avgMinutes / 60)}h ${Math.round(avgMinutes % 60)}m` : "-",
    },
  ];

  const periodLabel = selectedDate
    ? formatHrmsDate(selectedDate)
    : selectedMonth && selectedYear
      ? DateTime.fromObject({ year: Number(selectedYear), month: Number(selectedMonth) }).toFormat("LLLL yyyy")
      : "All time";

  return (
    <HrmsPageShell
      title={isTeamView ? "My Team Attendance" : "My Attendance"}
      description={
        isTeamView
          ? canViewAllEmployees
            ? "Attendance for every employee"
            : "Attendance for your direct reports"
          : "Your daily login/logout record"
      }
      sectionKey="attendance"
      noPermission={isTeamView && !canViewScopedList}
      isLoading={isLoading}
    >
      <HrmsCard mb={4} py={3}>
        <Flex align="flex-end" gap={3} wrap="wrap">
          {showTeamList && (
            <FormControl w={{ base: "100%", md: "240px" }}>
              <FormLabel fontSize="12px" color="gray.500" mb={1}>
                Employee
              </FormLabel>
              <Select
                size="sm"
                borderRadius="8px"
                placeholder={canViewAllEmployees ? "All employees" : "All team members"}
                onChange={(e) => setSelectedEmail(e.target.value)}
                value={selectedEmail}
              >
                {teamMembers.map((member) => (
                  <option key={member.email} value={member.email}>
                    {member.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl w={{ base: "100%", md: "200px" }}>
            <FormLabel fontSize="12px" color="gray.500" mb={1}>
              Month
            </FormLabel>
            <Select
              size="sm"
              borderRadius="8px"
              placeholder="All months"
              value={selectedMonth ? `${selectedMonth}-${selectedYear}` : ""}
              onChange={(e) => {
                const selected = generateMonthYearArray().find(
                  (item) => `${item.month}-${item.year}` === e.target.value,
                );
                setSelectedMonth(selected ? selected.month : "");
                setSelectedYear(selected ? selected.year : "");
              }}
            >
              {generateMonthYearArray().map((item) => (
                <option key={`${item.month}-${item.year}`} value={`${item.month}-${item.year}`}>
                  {item.text}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl w={{ base: "100%", md: "180px" }}>
            <FormLabel fontSize="12px" color="gray.500" mb={1}>
              Specific date
            </FormLabel>
            <Input
              size="sm"
              borderRadius="8px"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </FormControl>
          <Button
            size="sm"
            bg="#327171"
            color="white"
            _hover={{ bg: "#285e5e" }}
            px={6}
            onClick={search}
          >
            Apply
          </Button>
          {(selectedDate || selectedEmail) && (
            <Button
              size="sm"
              variant="ghost"
              color="gray.600"
              onClick={() => {
                setSelectedDate("");
                setSelectedEmail("");
              }}
            >
              Clear
            </Button>
          )}
        </Flex>
      </HrmsCard>

      <Text fontSize="13px" fontWeight="700" color="#495057" mb={2}>
        {periodLabel}
      </Text>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
        {stats.map((stat) => (
          <HrmsStatCard
            key={stat.label}
            label={stat.label}
            value={stat.value}
            accent={stat.accent && stat.value ? stat.accent : undefined}
            helpText={stat.help}
          />
        ))}
      </SimpleGrid>

      <HrmsTable
        head={[
          ...(isTeamView ? ["Employee"] : []),
          "Date",
          "Login → Logout",
          "Worked",
          "Day",
          "Flags",
        ]}
        isEmpty={records.length === 0}
        emptyText="No attendance records for this period"
      >
        {records.map((record) => {
          const flags = [
            record.isLate && (
              <Badge key="late" colorScheme="red" borderRadius="full" px={2} fontSize="10px">
                {formatHoursMinutes(record.lateByMinutes)} late
              </Badge>
            ),
            record.isEarlyExit && (
              <Badge key="early" colorScheme="orange" borderRadius="full" px={2} fontSize="10px">
                {formatHoursMinutes(record.earlyExitByMinutes)} early
              </Badge>
            ),
            !!record.overtimeMinutes && (
              <Badge key="ot" colorScheme="teal" borderRadius="full" px={2} fontSize="10px">
                +{formatHoursMinutes(record.overtimeMinutes)} OT
              </Badge>
            ),
          ].filter(Boolean);
          const name = (record.name || nameByEmail[record.email] || "").trim();
          return (
            <tr key={record._id}>
              {isTeamView && (
                <td>
                  <PersonCell name={name || record.email} subtitle={record.email} />
                </td>
              )}
              <td style={{ whiteSpace: "nowrap" }}>
                <Text fontSize="13px" fontWeight="600" m={0}>
                  {formatDate(record.date)}
                </Text>
                <Text fontSize="11px" color="gray.500" m={0}>
                  {record.date ? DateTime.fromISO(record.date).toFormat("cccc") : ""}
                </Text>
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                {formatTime(record?.loginTime)}
                <Text as="span" color="gray.400" mx={1.5}>
                  →
                </Text>
                {record?.logoutTime ? (
                  formatTime(record.logoutTime)
                ) : (
                  <Text as="span" color="orange.500" fontSize="12px">
                    not logged out
                  </Text>
                )}
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                {calculateDuration(record?.loginTime, record?.logoutTime)}
              </td>
              <td>
                {record.dayType ? (
                  <Badge
                    colorScheme={DAY_TYPE_BADGE[record.dayType].color}
                    borderRadius="full"
                    px={2}
                    fontSize="10px"
                  >
                    {DAY_TYPE_BADGE[record.dayType].label}
                  </Badge>
                ) : (
                  "-"
                )}
              </td>
              <td>
                {flags.length ? (
                  <Flex gap={1.5} wrap="wrap">
                    {flags}
                  </Flex>
                ) : (
                  <Text as="span" color="gray.400" fontSize="12px">
                    On time
                  </Text>
                )}
              </td>
            </tr>
          );
        })}
      </HrmsTable>
    </HrmsPageShell>
  );
};

export const TeamAttendance = () => <EmployeeAttendance view="team" />;
